import { QueryConfig } from "react-query";
import { EnabledExperimentsResponse } from "../../types/experiments";
import { useAnonymousExperiments, useEnabledExperiments } from "../experiment";
import { useWSQuery } from "../helpers";

export const experimentFlags = {
  qboOnboarding: false,
  editAllInvoices: false,
  nextGenSubscription: false,
  qboContactsControl: false,
  bankingCards: false,
  wallet: false,
  qboSyncHistory: false,
  customPayablesColumns: false,
  collaboratorGroupsUI: false,
  collaboratorOnboardingRequirements: false,
  payoutSettings: false,
  purchaseOrderNumber: false,
  deduplicateCollaboratorList: false,
  requirementsBuilder: false,
  physicalCard: false,
  physicalCardReplace: false,
  bulkPayableDeductions: false,
  payrollGroupsOnTable: false,
  deductions: true,
  electronic1099ConsentRequired: false,
  nec1099Filing: false,
  rtpAccounts: false,
  giftCards: false,
  verificationsByFeature: false
};

export const anonExperimentFlags = {};

type ExperimentFlags = typeof experimentFlags;
type AnonExperimentFlags = typeof anonExperimentFlags;

export interface IFeatureFlags extends ExperimentFlags, AnonExperimentFlags {}

const transformExperimentsData = (
  anonymousExperiments: EnabledExperimentsResponse,
  enabledExperiments: EnabledExperimentsResponse,
  defaultExperiments:
    | typeof experimentFlags
    | typeof anonExperimentFlags
    | (typeof experimentFlags & typeof anonExperimentFlags)
) => {
  const anonymousFeatureFlags = anonymousExperiments.reduce<string[]>(
    (result, { featureFlags }) => result.concat(featureFlags),
    []
  );

  const enabledFeatureFlags = enabledExperiments.reduce<string[]>(
    (result, { featureFlags }) => result.concat(featureFlags),
    []
  );

  const allFeatureFlags = [...anonymousFeatureFlags, ...enabledFeatureFlags];

  const newState = Object.keys(defaultExperiments).reduce(
    (acc: any, name: string) => {
      if (allFeatureFlags?.includes(name)) {
        acc[name as any] = true;
      }
      return acc;
    },
    { ...defaultExperiments }
  );

  return { ...newState };
};

export const QUERY_FEATURE_FLAGS = "QUERY_FEATURE_FLAGS";
export const QUERY_ANONYMOUS_FEATURE_FLAGS = "QUERY_ANONYMOUS_FEATURE_FLAGS";

export const useAnonymousFeatureFlags = (
  queryConfig?: QueryConfig<typeof anonExperimentFlags, unknown>
) => {
  const anonymousExperimentsQuery = useAnonymousExperiments();

  return useWSQuery(
    [QUERY_ANONYMOUS_FEATURE_FLAGS, anonymousExperimentsQuery.data],
    async () => {
      const result = transformExperimentsData(
        anonymousExperimentsQuery.data || [],
        [],
        anonExperimentFlags
      );

      return result as typeof anonExperimentFlags;
    },
    {
      ...queryConfig,
      enabled: anonymousExperimentsQuery.data
    }
  );
};

export const useFeatureFlags = (
  queryConfig?: QueryConfig<
    typeof experimentFlags & typeof anonExperimentFlags,
    unknown
  >
) => {
  const anonymousExperimentsQuery = useAnonymousExperiments();
  const enabledExperimentsQuery = useEnabledExperiments();

  return useWSQuery(
    [
      QUERY_FEATURE_FLAGS,
      anonymousExperimentsQuery.data,
      enabledExperimentsQuery.data
    ],
    async () => {
      const result = transformExperimentsData(
        anonymousExperimentsQuery.data || [],
        enabledExperimentsQuery.data || [],
        { ...experimentFlags, ...anonExperimentFlags }
      );

      return result as typeof experimentFlags & typeof anonExperimentFlags;
    },
    {
      ...queryConfig,
      enabled: anonymousExperimentsQuery.data && enabledExperimentsQuery.data
    }
  );
};
